import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Users, Plus, Pencil, Trash2, Shield, Copy, Check,
  AlertCircle, UserCheck, UserX, ChevronDown, ChevronUp,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'
import { fetchJsonAuth, postJsonAuth, authFetch } from '@/api/fetchWithAuth'
import type { PortalUser, AccessLevel, PortalSection, AuditLogEntry } from '@/types'

const BASE = '/api/portal'

const ALL_SECTIONS: { key: PortalSection; label: string; maxLevel: AccessLevel }[] = [
  { key: 'stammdaten', label: 'Stammdaten', maxLevel: 'write' },
  { key: 'studiengaenge', label: 'Studiengänge', maxLevel: 'write' },
  { key: 'noten', label: 'Noten', maxLevel: 'read' },
  { key: 'kontakt', label: 'Kontaktdaten', maxLevel: 'write' },
  { key: 'vorlesungstermine', label: 'Vorlesungstermine', maxLevel: 'read' },
  { key: 'zahlungen', label: 'Zahlungen', maxLevel: 'write' },
  { key: 'rechnungen', label: 'Rechnungen', maxLevel: 'read' },
  { key: 'ansprechpartner', label: 'Ansprechpartner', maxLevel: 'write' },
]

// ═══════════════════════════════════════════════════════════
//  AdminPage – Hauptkomponente
// ═══════════════════════════════════════════════════════════

export default function AdminPage() {
  const { user: authUser } = useAuth()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState<'users' | 'audit'>('users')
  const [showForm, setShowForm] = useState(false)
  const [editUser, setEditUser] = useState<number | null>(null)
  const [expandedUser, setExpandedUser] = useState<number | null>(null)

  const { data: usersData, isLoading, error, refetch } = useQuery({
    queryKey: ['portal-users'],
    queryFn: () => fetchJsonAuth<{ users: PortalUser[] }>(`${BASE}/users`),
  })

  const deleteMutation = useMutation({
    mutationFn: (userId: number) => authFetch(`${BASE}/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['portal-users'] }) },
  })

  if (isLoading) return <Spinner className="mt-20" />
  if (error) return <ErrorAlert message={(error as Error).message} onRetry={refetch} />

  const users = usersData?.users || []

  return (
    <div className="px-6 py-6">
      {/* Header */}
      <div className="bg-white border border-gray-200 rounded-lg px-6 py-5 mb-5 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-[1.35rem] font-bold text-gray-900 tracking-tight">
            <Shield size={20} className="inline mr-2 text-fom-500" />
            Administration
          </h1>
          <p className="text-gray-500 text-[0.78rem] mt-0.5">
            Benutzerverwaltung & Berechtigungen – {authUser?.DisplayName ?? ''}
          </p>
        </div>
      </div>

      {/* Tab-Switcher */}
      <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
            activeTab === 'users' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users size={14} className="inline mr-1.5" />
          Benutzer ({users.length})
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
            activeTab === 'audit' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Aktivitäten
        </button>
      </div>

      {activeTab === 'users' && (
        <>
          {/* Neuer User Button */}
          <div className="mb-4">
            <button onClick={() => { setShowForm(true); setEditUser(null) }} className="btn btn-primary">
              <Plus size={14} />
              Neuer Benutzer
            </button>
          </div>

          {/* User-Form Dialog */}
          {showForm && (
            <UserForm
              userId={editUser}
              onClose={() => { setShowForm(false); setEditUser(null) }}
              onSaved={() => {
                setShowForm(false)
                setEditUser(null)
                qc.invalidateQueries({ queryKey: ['portal-users'] })
              }}
            />
          )}

          {/* User-Tabelle */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Benutzer</th>
                  <th className="px-4 py-3 text-left">E-Mail</th>
                  <th className="px-4 py-3 text-center">Rolle</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Alle TN</th>
                  <th className="px-4 py-3 text-center">Letzter Login</th>
                  <th className="px-4 py-3 text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <UserRow
                    key={u.Id}
                    user={u}
                    isExpanded={expandedUser === u.Id}
                    onToggle={() => setExpandedUser(expandedUser === u.Id ? null : u.Id)}
                    onEdit={() => { setEditUser(u.Id); setShowForm(true) }}
                    onDelete={() => {
                      if (confirm(`${u.DisplayName} wirklich löschen?`)) {
                        deleteMutation.mutate(u.Id)
                      }
                    }}
                    isSelf={u.Id === authUser?.Id}
                  />
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center text-gray-400 py-10 text-sm">Keine Benutzer vorhanden</div>
            )}
          </div>
        </>
      )}

      {activeTab === 'audit' && <AuditLog />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
//  UserRow
// ═══════════════════════════════════════════════════════════

function UserRow({
  user, isExpanded, onToggle, onEdit, onDelete, isSelf,
}: {
  user: PortalUser
  isExpanded: boolean
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
  isSelf: boolean
}) {
  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3">
          <button onClick={onToggle} className="flex items-center gap-2 text-left">
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span className="font-medium text-gray-900">{user.DisplayName}</span>
            {isSelf && <span className="text-[10px] bg-fom-100 text-fom-600 px-1.5 py-0.5 rounded-full">Du</span>}
          </button>
        </td>
        <td className="px-4 py-3 text-gray-500">{user.Email}</td>
        <td className="px-4 py-3 text-center">
          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${
            user.Role === 'admin'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-gray-100 text-gray-600'
          }`}>
            {user.Role === 'admin' ? 'Admin' : 'User'}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          {user.IsActive ? (
            <UserCheck size={16} className="inline text-green-500" />
          ) : (
            <UserX size={16} className="inline text-red-400" />
          )}
        </td>
        <td className="px-4 py-3 text-center text-gray-500 text-xs">
          {user.SeeAllTeilnehmer ? '✓ Alle' : 'Whitelist'}
        </td>
        <td className="px-4 py-3 text-center text-gray-400 text-xs">
          {user.LastLogin ? new Date(user.LastLogin).toLocaleDateString('de-DE') : '–'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Bearbeiten">
              <Pencil size={14} />
            </button>
            {!isSelf && (
              <button onClick={onDelete} className="p-1.5 rounded-md hover:bg-red-50 text-gray-400 hover:text-red-500" title="Löschen">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="px-6 py-4 bg-gray-50">
            <UserPermissionsDetail userId={user.Id} />
          </td>
        </tr>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
//  UserPermissionsDetail (inline expandable)
// ═══════════════════════════════════════════════════════════

function UserPermissionsDetail({ userId }: { userId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['portal-user', userId],
    queryFn: () => fetchJsonAuth<{ user: PortalUser; permissions: Record<string, string>; teilnehmer_ids: number[] }>(`${BASE}/users/${userId}`),
  })

  if (isLoading) return <Spinner />
  if (!data) return null

  const { permissions, teilnehmer_ids } = data

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Berechtigungen</h4>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {ALL_SECTIONS.map((s) => {
          const level = (permissions[s.key] || 'none') as AccessLevel
          return (
            <div key={s.key} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-200">
              <span className="text-xs text-gray-600">{s.label}</span>
              <LevelBadge level={level} />
            </div>
          )
        })}
      </div>
      {teilnehmer_ids.length > 0 && (
        <p className="text-xs text-gray-400">{teilnehmer_ids.length} Teilnehmer zugeordnet</p>
      )}
    </div>
  )
}

function LevelBadge({ level }: { level: AccessLevel }) {
  const cls = level === 'write'
    ? 'bg-green-100 text-green-700'
    : level === 'read'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-400'
  const label = level === 'write' ? 'Schreiben' : level === 'read' ? 'Lesen' : 'Kein'
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>{label}</span>
  )
}

// ═══════════════════════════════════════════════════════════
//  UserForm (Create / Edit)
// ═══════════════════════════════════════════════════════════

function UserForm({
  userId, onClose, onSaved,
}: {
  userId: number | null
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = userId !== null

  const { data: editData, isLoading: editLoading } = useQuery({
    queryKey: ['portal-user', userId],
    queryFn: () => fetchJsonAuth<{ user: PortalUser; permissions: Record<string, string>; teilnehmer_ids: number[] }>(`${BASE}/users/${userId}`),
    enabled: isEdit,
  })

  const [email, setEmail] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<'superadmin' | 'admin' | 'user'>('user')
  const [seeAll, setSeeAll] = useState(false)
  const [isActive, setIsActive] = useState(true)
  const [perms, setPerms] = useState<Record<string, string>>({})
  const [error, setError] = useState<string | null>(null)
  const [inviteUrl, setInviteUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Daten laden wenn Edit
  if (isEdit && editData && !email) {
    const u = editData.user
    setEmail(u.Email)
    setDisplayName(u.DisplayName)
    setRole(u.Role)
    setSeeAll(u.SeeAllTeilnehmer)
    setIsActive(u.IsActive)
    setPerms(editData.permissions)
  }

  const setPerm = (section: string, level: string) => {
    setPerms((p) => ({ ...p, [section]: level }))
  }

  const handleSubmit = async () => {
    setError(null)
    try {
      if (isEdit) {
        await postJsonAuth(`${BASE}/users/${userId}`, {
          email, display_name: displayName, role,
          see_all_teilnehmer: seeAll, is_active: isActive,
        }, 'PUT')
        await postJsonAuth(`${BASE}/users/${userId}/permissions`, { permissions: perms }, 'PUT')
      } else {
        const result = await postJsonAuth<{ id: number; invite_url: string }>(`${BASE}/users`, {
          email, display_name: displayName, role,
          see_all_teilnehmer: seeAll, permissions: perms,
        })
        setInviteUrl(result.invite_url)
        return // Nicht schließen – Einladungslink zeigen
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler')
    }
  }

  if (editLoading) return <div className="bg-white border rounded-lg p-6 mb-4"><Spinner /></div>

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4 shadow-sm">
      <h3 className="text-base font-bold text-gray-900 mb-4">
        {isEdit ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
      </h3>

      {error && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-4 text-sm">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {inviteUrl && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-green-800 font-medium mb-2">✓ Benutzer angelegt! Einladungslink:</p>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={`${window.location.origin}${inviteUrl}`}
              className="flex-1 px-3 py-1.5 text-xs bg-white border rounded-lg font-mono"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}${inviteUrl}`)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className="btn btn-outline"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Kopiert!' : 'Kopieren'}
            </button>
          </div>
          <button onClick={onSaved} className="btn btn-primary mt-3">Schließen</button>
        </div>
      )}

      {!inviteUrl && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">E-Mail</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none"
                placeholder="user@firma.de"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Anzeigename</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none"
                placeholder="Max Mustermann"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-6">
            <label className="flex items-center gap-2 text-sm">
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'user')}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
              Rolle
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={seeAll} onChange={(e) => setSeeAll(e.target.checked)}
                     className="rounded border-gray-300 text-fom-500 focus:ring-fom-500" />
              Alle Teilnehmer sichtbar
            </label>
            {isEdit && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)}
                       className="rounded border-gray-300 text-fom-500 focus:ring-fom-500" />
                Aktiv
              </label>
            )}
          </div>

          {/* Permissions Matrix */}
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Berechtigungen</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-6">
            {ALL_SECTIONS.map((s) => (
              <div key={s.key} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                <span className="text-xs font-medium text-gray-700">{s.label}</span>
                <div className="flex gap-1">
                  {(['none', 'read', 'write'] as const).map((lvl) => {
                    const disabled = lvl === 'write' && s.maxLevel === 'read'
                    const active = (perms[s.key] || 'none') === lvl
                    return (
                      <button
                        key={lvl}
                        disabled={disabled}
                        onClick={() => setPerm(s.key, lvl)}
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold transition-all ${
                          active
                            ? lvl === 'write' ? 'bg-green-500 text-white'
                            : lvl === 'read' ? 'bg-blue-500 text-white'
                            : 'bg-gray-400 text-white'
                            : disabled ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {lvl === 'none' ? '✕' : lvl === 'read' ? '👁' : '✎'}
                      </button>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <button onClick={handleSubmit} className="btn btn-primary">
              {isEdit ? 'Speichern' : 'Benutzer anlegen'}
            </button>
            <button onClick={onClose} className="btn btn-outline">Abbrechen</button>
          </div>
        </>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
//  AuditLog
// ═══════════════════════════════════════════════════════════

function AuditLog() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['portal-audit'],
    queryFn: () => fetchJsonAuth<{ audit_log: AuditLogEntry[]; count: number }>(`${BASE}/audit`),
  })

  if (isLoading) return <Spinner className="mt-10" />
  if (error) return <ErrorAlert message={(error as Error).message} onRetry={refetch} />

  const entries = data?.audit_log || []

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
            <th className="px-4 py-3 text-left">Zeitpunkt</th>
            <th className="px-4 py-3 text-left">Aktion</th>
            <th className="px-4 py-3 text-left">User-ID</th>
            <th className="px-4 py-3 text-left">Ziel</th>
            <th className="px-4 py-3 text-left">IP</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map((e) => (
            <tr key={e.Id} className="hover:bg-gray-50 text-xs">
              <td className="px-4 py-2.5 text-gray-500">
                {new Date(e.CreatedAt).toLocaleString('de-DE')}
              </td>
              <td className="px-4 py-2.5 font-medium text-gray-900">{e.Action}</td>
              <td className="px-4 py-2.5 text-gray-500">{e.UserId ?? '–'}</td>
              <td className="px-4 py-2.5 text-gray-500">
                {e.TargetType ? `${e.TargetType} #${e.TargetId}` : '–'}
              </td>
              <td className="px-4 py-2.5 text-gray-400">{e.IpAddress ?? '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {entries.length === 0 && (
        <div className="text-center text-gray-400 py-10 text-sm">Keine Aktivitäten</div>
      )}
    </div>
  )
}
